import React, { useState } from 'react';
import { PackageItemState, BomItemState } from '../../../../../types/lender-packages.types';
import { NumericInput } from '../../../../../components/shared/ui/NumericInput';
import {
  PACKAGE_ITEM_TYPE_COLORS,
  DEFAULT_PACKAGE_ITEM_COLORS,
  PACKAGE_ITEM_TYPE_PRIORITY,
} from '../../../../../constants/lender-packages.constants';

interface LenderPackagesTableProps {
  items: PackageItemState[];
  isEnabled: boolean;
  packageKey?: string;
  onToggleAllBomItems: (itemId: string, enabled: boolean) => void;
  onToggleBomItem: (itemId: string, bomItemId: string) => void;
  onBomItemQuantityChange: (itemId: string, bomItemId: string, quantity: number) => void;
}

const getTypeColors = (itemType: string): typeof DEFAULT_PACKAGE_ITEM_COLORS =>
  PACKAGE_ITEM_TYPE_COLORS[itemType.toLowerCase()] ?? DEFAULT_PACKAGE_ITEM_COLORS;

const getTypePriority = (itemType: string): number =>
  PACKAGE_ITEM_TYPE_PRIORITY[itemType.toLowerCase()] ?? 99;

export const LenderPackagesTable: React.FC<LenderPackagesTableProps> = ({
  items,
  isEnabled,
  packageKey,
  onToggleAllBomItems,
  onToggleBomItem,
  onBomItemQuantityChange,
}) => {
  const [search, setSearch] = useState('');

  const activeBomCount = items.reduce((sum, item) => sum + item.bom_items.filter(b => b.enabled).length, 0);
  const totalBomCount = items.reduce((sum, item) => sum + item.bom_items.length, 0);

  const allGlobalEnabled = totalBomCount > 0 && activeBomCount === totalBomCount;

  const handleToggleAll = (): void => {
    const enable = !allGlobalEnabled;
    items.forEach(item => onToggleAllBomItems(item.id, enable));
  };

  const filteredItems = (search.trim()
    ? items.map(item => ({
        ...item,
        bom_items: item.bom_items.filter(b =>
          b.bom_sku?.toLowerCase().includes(search.toLowerCase()) ||
          b.bom_mpn?.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(item => item.bom_items.length > 0)
    : items
  ).slice().sort((a, b) => getTypePriority(a.item_type) - getTypePriority(b.item_type));

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2.5 px-3 py-2.5 bg-gradient-to-r from-slate-100 to-slate-50 border-b border-slate-200">
        <div className="w-5 h-5 rounded-md bg-blue-500 flex items-center justify-center shrink-0">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <span className="text-xs 3xl:text-sm font-bold uppercase tracking-wider text-slate-700 shrink-0">Package Items</span>
        {packageKey && (
          <span className="font-mono text-[10px] 3xl:text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200/60 rounded-full px-2.5 py-0.5 shrink-0">{packageKey}</span>
        )}

        {/* Search */}
        <div className="relative ml-auto flex-1 max-w-44">
          <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-300 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full pl-6 pr-2 py-1 text-[10px] 3xl:text-xs rounded-md border border-slate-200 bg-white text-slate-600 placeholder-slate-300 focus:outline-none focus:border-blue-300 focus:ring-1 focus:ring-blue-300 transition-colors"
          />
        </div>

        {/* Counter */}
        <div className="flex items-center gap-0.5 shrink-0">
          <span className="text-[10px] 3xl:text-xs font-bold text-blue-600">{activeBomCount}</span>
          <span className="text-[9px] 3xl:text-[10px] text-slate-400">/ {totalBomCount}</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200/80 bg-slate-50/40">
              <th className="px-2 py-1.5 text-left align-middle w-8">
                <button
                  type="button"
                  onClick={handleToggleAll}
                  disabled={!isEnabled || totalBomCount === 0}
                  className="text-[10px] 3xl:text-xs font-bold uppercase tracking-wider text-blue-500 hover:text-blue-700 disabled:text-slate-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {allGlobalEnabled ? 'All' : 'All'}
                </button>
              </th>
              <th className="px-2 py-1.5 text-left align-middle text-[10px] 3xl:text-xs font-bold uppercase tracking-wider text-slate-400">Kind</th>
              <th className="px-2 py-1.5 text-left text-[10px] 3xl:text-xs font-bold uppercase tracking-wider text-slate-400">SKU</th>
              <th className="px-2 py-1.5 text-left text-[10px] 3xl:text-xs font-bold uppercase tracking-wider text-slate-400">Make</th>
              <th className="px-2 py-1.5 text-left text-[10px] 3xl:text-xs font-bold uppercase tracking-wider text-slate-400">MPN</th>
              <th className="px-2 py-1.5 text-center text-[10px] 3xl:text-xs font-bold uppercase tracking-wider text-slate-400 w-14">Qty</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map(item => (
              <ItemGroup
                key={item.id}
                item={item}
                isEnabled={isEnabled}
                onToggleAllBomItems={onToggleAllBomItems}
                onToggleBomItem={onToggleBomItem}
                onBomItemQuantityChange={onBomItemQuantityChange}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface ItemGroupProps {
  item: PackageItemState;
  isEnabled: boolean;
  onToggleAllBomItems: (itemId: string, enabled: boolean) => void;
  onToggleBomItem: (itemId: string, bomItemId: string) => void;
  onBomItemQuantityChange: (itemId: string, bomItemId: string, quantity: number) => void;
}

const ItemGroup: React.FC<ItemGroupProps> = ({
  item,
  isEnabled,
  onToggleAllBomItems,
  onToggleBomItem,
  onBomItemQuantityChange,
}) => {
  const allEnabled = item.bom_items.length > 0 && item.bom_items.every(b => b.enabled);
  const someEnabled = item.bom_items.some(b => b.enabled);
  const colors = getTypeColors(item.item_type);
  const enabledCount = item.bom_items.filter(b => b.enabled).length;

  return (
    <>
      {/* Group header */}
      <tr className={`bg-gradient-to-r ${colors.bg} border-b border-slate-100`}>
        <td colSpan={6} className="px-2 py-1.5">
          <div className="flex items-center gap-2">
            <ToggleSwitch
              allEnabled={allEnabled}
              someEnabled={someEnabled}
              isEnabled={isEnabled && item.bom_items.length > 0}
              onToggle={() => onToggleAllBomItems(item.id, !allEnabled)}
              small
            />
            <span className={`text-[10px] 3xl:text-xs font-bold uppercase tracking-wider px-1.5 py-px rounded border ${colors.badge}`}>
              {item.item_type}
            </span>
            {item.equipment_label && (
              <span className="text-[10px] 3xl:text-xs text-slate-400 font-medium truncate">{item.equipment_label}</span>
            )}
            <span className="text-[10px] text-slate-300 ml-auto tabular-nums">{enabledCount}/{item.bom_items.length}</span>
          </div>
        </td>
      </tr>

      {/* BOM rows */}
      {item.bom_items.map((bom, idx) => (
        <BomRow
          key={bom.id}
          bom={bom}
          itemId={item.id}
          make={item.make}
          isLast={idx === item.bom_items.length - 1}
          isEnabled={isEnabled}
          borderColor={colors.border}
          onToggle={() => onToggleBomItem(item.id, bom.id)}
          onQuantityChange={(qty) => onBomItemQuantityChange(item.id, bom.id, qty)}
        />
      ))}
    </>
  );
};

interface BomRowProps {
  bom: BomItemState;
  itemId: string;
  make: string;
  isLast: boolean;
  isEnabled: boolean;
  borderColor: string;
  onToggle: () => void;
  onQuantityChange: (quantity: number) => void;
}

const BomRow: React.FC<BomRowProps> = ({
  bom, make, isLast, isEnabled, borderColor, onToggle, onQuantityChange,
}) => {
  const off = !bom.enabled;
  const opacity = off ? 'opacity-30' : '';
  const border = isLast ? 'border-b border-slate-200/60' : 'border-b border-slate-50';

  return (
    <tr className={`${border} border-l-2 ${borderColor} transition-colors ${off ? 'bg-white' : 'hover:bg-slate-50/50'}`}>
      <td className="px-2 py-1 pl-6">
        <ToggleSwitch
          allEnabled={bom.enabled}
          someEnabled={false}
          isEnabled={isEnabled}
          onToggle={onToggle}
          small
        />
      </td>
      <td className={`px-2 py-1 ${opacity}`}>
        <span className="text-[10px] 3xl:text-xs font-medium text-slate-500 bg-slate-50 rounded px-1 py-px">
          {bom.bom_kind}
        </span>
      </td>
      <td className={`px-2 py-1 font-mono text-[10px] 3xl:text-xs text-slate-500 ${opacity}`}>
        {bom.bom_sku || <span className="text-slate-200">&mdash;</span>}
      </td>
      <td className={`px-2 py-1 text-[10px] 3xl:text-xs text-slate-600 ${opacity}`}>
        {make || <span className="text-slate-200">&mdash;</span>}
      </td>
      <td className={`px-2 py-1 ${opacity}`}>
        <ModelCell mpn={bom.bom_mpn} description={bom.bom_description} />
      </td>
      <td className={`px-2 py-1 text-center ${opacity}`}>
        <NumericInput
          value={bom.quantity}
          onChange={onQuantityChange}
          min={0}
          max={999}
          disabled={!isEnabled || off}
          className="w-10 rounded border border-slate-200 bg-white px-1 py-0.5 text-[10px] 3xl:text-xs text-center font-semibold text-slate-700 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none disabled:bg-slate-50 disabled:text-slate-300 transition-colors"
        />
      </td>
    </tr>
  );
};

interface ModelCellProps {
  mpn: string;
  description: string;
}

const ModelCell: React.FC<ModelCellProps> = ({ mpn, description }) => (
  <div className="relative group/tooltip inline-block max-w-full">
    <span className="text-[10px] 3xl:text-xs font-medium text-slate-700 cursor-default underline decoration-dotted decoration-slate-300 underline-offset-2 truncate block max-w-[200px]">
      {mpn || <span className="text-slate-200">&mdash;</span>}
    </span>
    {description && (
      <div className="pointer-events-none absolute bottom-full left-0 mb-1.5 z-10 hidden group-hover/tooltip:block">
        <div className="rounded-md bg-slate-800 px-2.5 py-1.5 text-[9px] 3xl:text-[10px] text-white shadow-lg max-w-xs whitespace-normal leading-relaxed">
          {description}
          <div className="absolute top-full left-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-800" />
        </div>
      </div>
    )}
  </div>
);

interface ToggleSwitchProps {
  allEnabled: boolean;
  someEnabled: boolean;
  isEnabled: boolean;
  onToggle: () => void;
  small?: boolean;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ allEnabled, someEnabled, isEnabled, onToggle, small }) => {
  const trackColor = allEnabled
    ? 'bg-blue-500'
    : someEnabled
      ? 'bg-blue-300'
      : 'bg-slate-200';

  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      disabled={!isEnabled}
      title={allEnabled ? 'Deselect' : 'Select'}
      className={`${small ? 'w-6 h-3.5' : 'w-7 h-4'} rounded-full relative shrink-0 transition-colors duration-200 ${trackColor} disabled:cursor-not-allowed disabled:opacity-40`}
    >
      <span
        className={`${small ? 'w-2.5 h-2.5' : 'w-3 h-3'} rounded-full bg-white shadow-sm absolute top-1/2 transition-all duration-200`}
        style={{
          transform: 'translateY(-50%)',
          left: allEnabled ? (small ? '12px' : '14px') : '2px',
        }}
      />
    </button>
  );
};
